import { Injectable } from '@angular/core';
import { CellData, ExcelGeneratorData } from 'src/app/dto/excel-generation-data.dto';
import { Order } from 'src/app/dto/order.dto';
import { firstValueFrom } from 'rxjs';
import { XlsxTemplateFillerServiceService } from './xlsx-template-filler-service.service';
import { DateTimeService } from './date-time.service';
import { DocumentsStorageService } from '../documents-storage.service';
import { FORWARDER_REPORT_TPL_PATH } from '../../utils/constants/document-template.constants';
import { DocumentFieldFormatter } from '../../utils/document-field-formatter.util';

@Injectable({
  providedIn: 'root'
})
export class ForwarderReportGeneratorService {
  private startIndex = 16;

  constructor(
    private xlsxTemplateFillerServiceService: XlsxTemplateFillerServiceService,
    private dateTimeService: DateTimeService,
    private readonly docStorageService: DocumentsStorageService
  ) {}

  async getForwarderReport(order: Order): Promise<void> {
    const data = await this.generateForwarderReportDataStructure(order);

    if (data) {
      await firstValueFrom(
        this.docStorageService.storeForwarderReport(data, {
          requestId: order.id
        })
      );
      await this.xlsxTemplateFillerServiceService.generateAndOpenNewExcelFile(
        FORWARDER_REPORT_TPL_PATH,
        data,
        `Отчет экспедитора-${order.id}`
      );
    }
    // TODO: обработка кейса нехватки данных для формирования документа
  }

  // async storeForwarderReport(order: Order): Promise<void> {
  //   const data = await this.generateForwarderReportDataStructure(order);

  // }

  private async generateForwarderReportDataStructure(
    order: Order
  ): Promise<ExcelGeneratorData | null> {
    if (!order.unloadedDeliveryInfo) {
      return null;
    }

    const cellDataArray: CellData[] = [];

    let recordCounter = 0;
    let totalWeight = 0;

    order.unloadedDeliveryInfo.unloadedDays.forEach(day => {
      day.vehicles.forEach(vehicle => {
        recordCounter += 1;
        const recordIndex = this.startIndex + recordCounter;

        const vehicleWeight = vehicle.unloadedWeight / 1000;
        totalWeight += vehicleWeight;

        cellDataArray.push({
          address: `A${recordIndex}`,
          value: recordCounter,
          name: 'number',
          pageNumber: 1
        });

        const operationNote = DocumentFieldFormatter.safeConcatenate([
          { value: `Перевозка ${vehicleWeight}т ${day.unloadedDate}.` },
          { value: `Пункт отгрузки ${DocumentFieldFormatter.safeValue(order.loading.title)}
           ${DocumentFieldFormatter.safeValue(order.loading.address)}.` },
          { value: `Пункт разгрузки: ${DocumentFieldFormatter.safeValue(order.unloading.title)}
           ${DocumentFieldFormatter.safeValue(order.unloading.address)}.` },
          { value: `Перевозчик: ${DocumentFieldFormatter.safeValue(vehicle.registrationNumber)},
           ${DocumentFieldFormatter.safeValue(vehicle.trailerRegistrationNumber, 'без прицепа')}` },
          { value: `на основании ТТН №${DocumentFieldFormatter.safeValue(vehicle.ttnId)}` }
        ], '\n      ');

        cellDataArray.push({
          address: `C${recordIndex}`,
          value: operationNote,
          name: 'operation-note',
          pageNumber: 1
        });

        cellDataArray.push({
          address: `AC${recordIndex}`,
          value: `
             ${order.loading.title} ${order.loading.address} -
             ${order.unloading.title} ${order.unloading.address}
            `,
          name: 'operation-note',
          pageNumber: 1
        });

        cellDataArray.push({
          address: `Q${recordIndex}`,
          value: `${vehicle.registrationNumber}`,
          name: 'forwarder-org',
          pageNumber: 1
        });

        cellDataArray.push({
          address: `AL${recordIndex}`,
          value: parseFloat(vehicleWeight.toFixed(2)),
          name: 'net-weight',
          pageNumber: 1
        });
      });
    });

    const numExtraRows: number = recordCounter - 1;
    const generateForwarderReportData: ExcelGeneratorData = {
      cellDataArray,
      additionalOptions: {
        copyRows: [{
          copyFromRow: 17,
          numRows: numExtraRows,
          pageNumber: 1
        }]
      }
    };

    // Итоговые значения
    generateForwarderReportData.cellDataArray.push({
      address: `AL${18 + numExtraRows}`,
      value: parseFloat(totalWeight.toFixed(3)),
      name: 'total-weight',
      pageNumber: 1
    });

    // агент это смарт. поле оставляем пустым
    // generateForwarderReportData.cellDataArray.push({
    //   address: 'BP1000',
    //   value: `${Number((totalWeight * order.forwarderFee))}`,
    //   name: 'total-fee',
    //   pageNumber: 1
    // });

    generateForwarderReportData.cellDataArray.push({
      address: 'AT4',
      value: `${order.id}-${Date.now().toString().slice(-5)}`,
      name: 'order-id',
      pageNumber: 1
    });

    generateForwarderReportData.cellDataArray.push({
      address: 'BB4',
      value: `${this.dateTimeService.getNowDateStringFormat()}`,
      name: 'created-date',
      pageNumber: 1
    });

    generateForwarderReportData.cellDataArray.push({
      address: 'AN7',
      value: `${order.contractName}`,
      name: 'order-id',
      pageNumber: 1
    });
    generateForwarderReportData.cellDataArray.push({
      address: 'AF9',
      value: `${order.contractName}`,
      name: 'order-id',
      pageNumber: 1
    });

    generateForwarderReportData.cellDataArray.push({
      address: 'AX7',
      value: `${this.dateTimeService.getNowDateStringFormat()}`,
      name: '',
      pageNumber: 1
    });

    if (order.createdDate) {
      generateForwarderReportData.cellDataArray.push({
        address: 'AK9',
        value: `${this.dateTimeService.getDateFromTimestamp(order.createdDate)}`,
        name: '',
        pageNumber: 1
      });
    }

    if (order.freightName) {
      generateForwarderReportData.cellDataArray.push({
        address: 'J11',
        value: `${order.freightName}`,
        name: 'freight-name',
        pageNumber: 1
      });
    }

    order.generateForwarderReportActions(
      generateForwarderReportData.cellDataArray
    );

    return generateForwarderReportData;
  }
}
